import { Box, Typography, Menu, MenuItem, ListItemIcon, ListItemText } from "@mui/material";
import { NavLink, useParams, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import WorkOutlineOutlinedIcon from "@mui/icons-material/WorkOutlineOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import { ReactNode, useState } from "react";
import TextSnippetOutlinedIcon from "@mui/icons-material/TextSnippetOutlined";
import { useOnlineStatus } from "utils/connectivity/hook/use-online-status";
import { costumPalette } from "theme/palette";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import FavoriteIcon from "@mui/icons-material/Favorite";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";

type Link = {
  id: number;
  path: string;
  text: string;
  icon: ReactNode;
};

// Main/Project Bottom Navigation Component
export const MainBottomNavigation = () => {
  const { t } = useTranslation();
  const { projectId } = useParams<"projectId">();
  const isOnline = useOnlineStatus();

  const projectLinks: Link[] = [
    {
      id: 1,
      path: projectId + "/dashboard",
      text: t("mobile.navigation.home"),
      icon: <HomeOutlinedIcon />,
    },
    {
      id: 2,
      path: projectId ?? "",
      text: t("mobile.navigation.piecework"),
      icon: <WorkOutlineOutlinedIcon />,
    },
    {
      id: 3,
      path: projectId + "/logbook",
      text: t("mobile.navigation.logbook"),
      icon: <CalendarMonthOutlinedIcon />,
    },
    {
      id: 4,
      path: projectId + "/draft",
      text: t("mobile.navigation.draft"),
      icon: <TextSnippetOutlinedIcon />,
    },
  ];

  return (
    <Box
      sx={{
        position: "fixed",
        top: "auto",
        bottom: 0,
        left: 0,
        width: "100%",
        height: "60px",
        display: "flex",
        justifyContent: "space-around",
        backgroundColor: "primary.dark",
        pt: "10px",
      }}
    >
      {projectLinks.map((link) => {
        return isOnline ? (
          <NavLink key={link.id} to={link.path} style={{ textDecoration: "none" }}>
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                color: costumPalette.white,
                opacity: "1",
              }}
            >
              {link.icon}
              <Typography variant="caption">{link.text}</Typography>
            </Box>
          </NavLink>
        ) : (
          <Box
            key={link.id}
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: costumPalette.white,
              opacity: link.id === 4 ? "1" : "0.5",
            }}
          >
            {link.icon}
            <Typography variant="caption">{link.text}</Typography>
          </Box>
        );
      })}
    </Box>
  );
};

// Account Bottom Navigation Component
export const AccountBottomNavigation = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isDropdownOpen = Boolean(anchorEl);

  const accountLinks: Link[] = [
    {
      id: 1,
      path: "/account/profile",
      text: t("account.menu.profile"),
      icon: <AccountCircleOutlinedIcon />,
    },
    {
      id: 2,
      path: "/account/notifications",
      text: t("account.notifications.title"),
      icon: <NotificationsNoneOutlinedIcon />,
    },
    {
      id: 3,
      path: "/account/projects",
      text: t("account.projects.title"),
      icon: <LocationCityIcon />,
    },
  ];

  const dropdownLinks: Link[] = [
    {
      id: 4,
      path: "/account/favorites",
      text: t("account.favorites.title"),
      icon: <FavoriteIcon />,
    },
    {
      id: 5,
      path: "/account/information",
      text: t("account.information.title"),
      icon: <InfoOutlinedIcon />,
    },
  ];

  const handleDropdownClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDropdownClose = () => {
    setAnchorEl(null);
  };

  const handleDropdownItemClick = (path: string) => {
    navigate(path);
    handleDropdownClose();
  };

  const isDropdownItemActive = () => {
    return dropdownLinks.some((link) => location.pathname === link.path);
  };

  return (
    <Box
      sx={{
        position: "fixed",
        top: "auto",
        bottom: 0,
        left: 0,
        width: "100%",
        height: "60px",
        display: "flex",
        justifyContent: "space-around",
        backgroundColor: "primary.dark",
        pt: "10px",
      }}
    >
      {/* Account navigation buttons */}
      {accountLinks.map((link) => (
        <NavLink key={link.id} to={link.path} style={{ textDecoration: "none" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              color: costumPalette.white,
            }}
          >
            {link.icon}
            <Typography variant="caption">{link.text}</Typography>
          </Box>
        </NavLink>
      ))}

      {/* More options button */}
      <Box
        onClick={handleDropdownClick}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          color: costumPalette.white,
          cursor: "pointer",
          opacity: isDropdownItemActive() ? 1 : 0.8,
        }}
      >
        <MoreHorizIcon />
        <Typography variant="caption">{t("mobile.navigation.more")}</Typography>
      </Box>

      {/* Dropdown menu */}
      <Menu
        anchorEl={anchorEl}
        open={isDropdownOpen}
        onClose={handleDropdownClose}
        anchorOrigin={{
          vertical: "top",
          horizontal: "center",
        }}
        transformOrigin={{
          vertical: "bottom",
          horizontal: "center",
        }}
        sx={{
          "& .MuiPaper-root": {
            backgroundColor: "primary.dark",
            color: costumPalette.white,
            minWidth: "180px",
            marginBottom: "10px",
          },
        }}
      >
        {dropdownLinks.map((link) => (
          <MenuItem
            key={link.id}
            onClick={() => handleDropdownItemClick(link.path)}
            sx={{
              color: costumPalette.white,
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.1)",
              },
            }}
          >
            <ListItemIcon sx={{ color: costumPalette.white }}>{link.icon}</ListItemIcon>
            <ListItemText>
              <Typography variant="body2">{link.text}</Typography>
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
};

// Main wrapper component that decides which navigation to show
export const MobileBottomNavigation = () => {
  const { projectId } = useParams<"projectId">();
  const location = useLocation();

  // Show project navigation when in a project
  if (projectId) {
    return <MainBottomNavigation />;
  }

  // Show account navigation when in account section
  if (location.pathname.startsWith("/account")) {
    return <AccountBottomNavigation />;
  }

  // Return null if neither condition is met
  return null;
};
